import React, { CSSProperties, useEffect, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Dropdown,
  getColor,
  Tooltip,
  Typography,
} from "fds";
import { Workgroup } from "../../types/workgroup";
import { useTranslation } from "react-i18next";
import useCheckboxGroup from "../../shared/hooks/useCheckboxGroup";

import useWorkgroupStore from "../../redux/dispatcher/useWorkgroupStore";
import { DocumentListReq, favoriteReq } from "../../types/files";
import { MeetingRequest } from "../../types/meeting";
import dayjs from "dayjs";
import classNames from "classnames";
interface WorkgroupCheckboxDropdownProps {
  classes?: string;
  style?: CSSProperties;
  buttonClick: number;
  tab?: number; // 0 : My Box표시 , 1 : My Box 미표시
  page?: string; // workgroupHistory: 워크그룹 관리자 페이지
  meetingFilter: favoriteReq | MeetingRequest | DocumentListReq;
  workgroups?: Workgroup[];
  changeFilterByKey: (key: string, val: any) => void;
  setButtonClick: (click: number) => void;
  changeDateFilter?: (newFilter: string) => void;
}

const WorkgroupCheckboxDropdown: React.FC<WorkgroupCheckboxDropdownProps> = ({
  buttonClick,
  workgroups,
  meetingFilter,
  setButtonClick,
  changeFilterByKey,
  tab,
  page,
  classes,
  style,
}) => {
  const { t } = useTranslation();
  const checkboxGroup = useCheckboxGroup<string>();
  const [validWorkgroupList, changeValidWorkgroupList] = useState(workgroups);
  const [isChanged, changeIsChanged] = useState(false);

  useEffect(() => {
    const newWorkgroup = workgroups?.filter(
      (item: Workgroup) =>
        (item.endDate === -1 || item.endDate > dayjs().valueOf()) &&
        item.workgroupType !== "HUB"
    );
    if ("workgroupIds" in meetingFilter) {
      checkboxGroup.handleAllClick(meetingFilter.workgroupIds);
    }
    if ("tagCodes" in meetingFilter) {
      checkboxGroup.handleAllClick(meetingFilter.tagCodes);
    }
    changeValidWorkgroupList(newWorkgroup);
  }, [workgroups, meetingFilter]);

  const checkAllCheckValid = (values: string[]) => {
    return (
      (tab === 0 &&
        values.length ===
          (validWorkgroupList ? validWorkgroupList?.length + 1 : 1)) ||
      (tab === 1 &&
        values.filter((item) => item !== "NONEWORKGROUP").length ===
          (validWorkgroupList ? validWorkgroupList?.length : 0))
    );
  };
  return (
    <Dropdown
      btnStyle="round"
      classes={"mg ml-12"}
      btnSize="rd"
      placeholder={t("common.location")}
      buttonWidth={290}
      showValue
      selectValue={
        ("workgroupIds" in meetingFilter
          ? meetingFilter.workgroupIds
          : meetingFilter.tagCodes
        ).length + ""
      }
      defaultValue="0"
      value={
        ("workgroupIds" in meetingFilter
          ? meetingFilter.workgroupIds
          : meetingFilter.tagCodes
        ).length === 0
          ? t("common.all")
          : ("workgroupIds" in meetingFilter
              ? meetingFilter.workgroupIds
              : meetingFilter.tagCodes
            ).filter((item) => item !== "NONEWORKGROUP" || tab === 0).length +
            ""
      }
      buttonClick={buttonClick}
    >
      <Box width={274} maxHeight={388} classes={"dropdown-checkbox"}>
        <Box
          height={26}
          width="100%"
          alignItems="center"
          justifyContent="space-between"
        >
          <Typography
            type="caption_rg"
            classes="mg ml-8"
            exactColor={getColor("COLOR_TEXT_SECONDARY")}
          >
            {t("common.location")}
          </Typography>
          <Button
            btnStyle="link"
            label={
              checkAllCheckValid(checkboxGroup.selected)
                ? t("common.unselectAll")
                : t("common.selectAll")
            }
            onClick={() =>
              checkAllCheckValid(checkboxGroup.selected)
                ? checkboxGroup.handleAllClick([])
                : checkboxGroup.handleAllClick([
                    ...(validWorkgroupList ?? []).map(
                      (item) => item.workgroupId
                    ),
                    "NONEWORKGROUP",
                  ])
            }
            textColor={getColor("COLOR_TEXT_BRAND")}
          />
        </Box>
        <Box classes="mg mt-4 mb-4 fav-workgroups">
          {page !== "workgroupHistory" && (
            <Checkbox
              label={"My Box"}
              disable={tab === 1}
              onClick={() => checkboxGroup.handleClick("NONEWORKGROUP")}
              check={tab !== 1 && checkboxGroup.isSelected("NONEWORKGROUP")}
            />
          )}
          {validWorkgroupList &&
            validWorkgroupList.length > 0 &&
            page !== "workgroupHistory" && (
              <Checkbox
                label={t("common.workgroup")}
                onClick={() =>
                  checkboxGroup.handleAllClick([
                    ...(checkboxGroup.isSelected("NONEWORKGROUP")
                      ? ["NONEWORKGROUP"]
                      : []
                    ).concat(
                      checkboxGroup.selected.filter(
                        (item) => item !== "NONEWORKGROUP"
                      ).length === validWorkgroupList.length
                        ? []
                        : validWorkgroupList.map((item) => item.workgroupId)
                    ),
                  ])
                }
                indeterminate={
                  checkboxGroup.selected.filter(
                    (item) => item !== "NONEWORKGROUP"
                  ).length > 0 &&
                  checkboxGroup.selected.filter(
                    (item) => item !== "NONEWORKGROUP"
                  ).length < validWorkgroupList.length
                }
                check={
                  checkboxGroup.selected.filter(
                    (item) => item !== "NONEWORKGROUP"
                  ).length === validWorkgroupList.length
                }
              />
            )}
          <Box
            classes={classNames({ "mg ml-28": page !== "workgroupHistory" })}
            direction="column"
            alignItems="flex-start"
          >
            {(validWorkgroupList ?? []).map((workgroup) => (
              <Checkbox
                label={workgroup.workgroupName}
                onClick={() => checkboxGroup.handleClick(workgroup.workgroupId)}
                check={checkboxGroup.isSelected(workgroup.workgroupId)}
                isLabelTooltip
              />
            ))}
          </Box>
        </Box>
        <Box classes="dropdown-checkbox--bottom">
          <Button
            label={t("common.cancel")}
            type="secondary"
            btnStyle="fill"
            onClick={() => {
              setButtonClick(buttonClick + 1);
              checkboxGroup.handleAllClick(
                ("workgroupIds" in meetingFilter
                  ? meetingFilter.workgroupIds
                  : meetingFilter.tagCodes
                ).length === 0
                  ? [
                      // ...workgroupState.simpleWorkgroupList.map(
                      //   (item: Workgroup) => item.workgroupId
                      // ),
                      // "NONEWORKGROUP",
                    ]
                  : "workgroupIds" in meetingFilter
                  ? meetingFilter.workgroupIds
                  : meetingFilter.tagCodes
              );
            }}
          />
          <Button
            label={t("common.apply")}
            classes="mg ml-10"
            btnStyle="fill"
            state={
              !isChanged && checkboxGroup.selected.length === 0
                ? "disable"
                : "default"
            }
            onClick={() => {
              setButtonClick(buttonClick + 1);
              changeFilterByKey("workgroupIds", checkboxGroup.selected);
              changeIsChanged(true);
            }}
          />
        </Box>
      </Box>
    </Dropdown>
  );
};
export default WorkgroupCheckboxDropdown;
